/**
 * Formats a numeric value or a string convertible to a number as a currency string.
 * It uses the Czech locale (`cs-CZ`). Handles string inputs like "200.00" or "200,00".
 * If a value is not provided (null/undefined), it returns only the currency symbol.
 * If the value is a string that cannot be converted to a number, it returns an empty string.
 *
 * @param {number | string | null | undefined} value - The value to format.
 * @param {string} [currencyCode="CZK"] - The ISO 4217 currency code (e.g., "CZK", "EUR").
 * @param {number} [min=2] - The minimum number of decimal places.
 * @param {number} [max=2] - The maximum number of decimal places.
 * @returns {string} The formatted currency string, the currency symbol, or an empty string.
 *
 * @example
 * formatCurrency("1234.5");
 * // Returns: "1 234,50 Kč"
 *
 * @example
 * formatCurrency("99,99", "EUR");
 * // Returns: "99,99 €"
 *
 * @example
 * // Returns an empty string for invalid input
 * formatCurrency("neplatná hodnota");
 * // Returns: ""
 *
 * @example
 * // Returns just the symbol if value is null
 * formatCurrency(null, "USD");
 * // Returns: "$"
 */
const formatCurrency = (value, currencyCode = "CZK", min = 2, max = 2) => {
  // 1. Handle case for returning only the symbol
  if (value == null) {
    const parts = new Intl.NumberFormat("cs-CZ", {
      style: "currency",
      currency: currencyCode,
    }).formatToParts(0);
    const currencySymbol = parts.find(
      (part) => part.type === "currency"
    )?.value;
    return currencySymbol || currencyCode;
  }

  // 2. Prepare and validate the numeric value
  let numericValue;
  if (typeof value === "string") {
    // Replace comma with a dot for robust parsing and convert to number
    numericValue = parseFloat(value.replace(",", "."));
  } else {
    numericValue = value; // Assumes it's already a number
  }

  // If conversion resulted in NaN (Not-a-Number), return an empty string
  if (isNaN(numericValue)) {
    return "";
  }

  // 3. Format the valid number
  if (max < min) {
    min = max;
  }

  return new Intl.NumberFormat("cs-CZ", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  }).format(numericValue);
};

export default formatCurrency;
